import { defineComponent as _defineComponent } from 'vue'
import { computed, onActivated, provide, ref } from 'vue';
import { storeToRefs } from 'pinia';

import AdUnit from '@/components/AdUnit.vue';
import Breadcrumbs from '@/features/sports/components/ui/Breadcrumbs.vue';
import MultiStepEventEncounterList from '@/features/sports/components/global/MultiStepEventEncounterList.vue';
import Header from '@/features/sports/components/ui/Header.vue';
import CompetitionEventsCalendarOld from '@/features/sports/components/overview/CompetitionEventsCalendarOld.vue';
import ProviderStreamsComparator from '@/features/sports/components/global/ProviderStreamsComparator.vue';
import SportsLayout from '@/features/sports/components/global/SportsLayout.vue';
import Subheading from '@/features/sports/components/ui/Subheading.vue';
import { isSameDate, shiftDateWithTimezone } from '@/helpers/date-helper';
import { getOverviewUrlBySport } from '@/routing/sports';
import { Platform, SportEventAggregation, SportEventBucketsEdgeV2, SportEventSorting } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';
import { SportRouteTypes, type SportCategory, type SportLocale } from '@/features/sports/types/enums';
import { SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BreadcrumbItem, SportPageType } from '@/features/sports/types/interfaces';
import {
	GetMultiStepSportOverviewDocument,
	GetMultiStepSportOverviewQuery,
	GetMultiStepSportOverviewQueryVariables,
} from '@/features/sports/graphql/queries/GetMultiStepSportOverview.query';
import { useQuery } from '@/helpers/composables/useApollo';
import { useRoute } from 'vue-router/composables';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'MotorsportOverview',
  setup(__props) {

const { country, language, localeObject, webLocale } = storeToRefs(useLanguageStore());
const { urlMetadata } = storeToRefs(useRoutingStore());
const { filters } = useFilterStore();
const { tc } = useI18n();

const route = useRoute();

const displayDate = ref(shiftDateWithTimezone(localeObject.value.timezone));
const filtersSport = computed(() => filters(CollectionType.SPORTS));

const motorsportCategory = computed(() => {
	return route.meta?.sport as SportCategory;
});

const motorsportTypes = computed(() => {
	return SportRouteTypes.categories[motorsportCategory.value];
});

/** Helps support single step components until the multistep refactor. */
const miniContext = computed(() => {
	return {
		sportType: motorsportTypes.value,
		sportCategory: motorsportCategory.value,
		pageType: SportPageType.SportOverview,
	} as const;
});

const queryVariables = computed(() => {
	return {
		language: language.value,
		country: country.value,
		platform: Platform.Web,
		groupBy: SportEventAggregation.Parent,
		// we need to additionally filter in the frontend for motorsport.
		// that's why we tune up the bucketSize, while keeping `first` low, because there is 'formula 1' as the only competition
		bucketSize: 10, // normally 30, but complexity at 200k
		first: 1,
		filter: {
			startAt: {
				from: displayDate.value.toISOString(),
				to: shiftDateWithTimezone(localeObject.value.timezone, 3, displayDate.value).toISOString(),
			},
			packages: filtersSport.value.providers ?? [],
			sports: miniContext.value.sportType,
		},
		timezoneOffset: 0,
		sortBy: SportEventSorting.Popularity,
	};
});
const enabled = computed(
	() => route.name === 'app.sports.subdomain.overview' && (route.meta!.data as string)?.includes('multistep')
);
const {
	result: overviewData,
	loading: isDataLoading,
	fetchMore,
} = useQuery<GetMultiStepSportOverviewQuery, GetMultiStepSportOverviewQueryVariables>(
	GetMultiStepSportOverviewDocument,
	queryVariables,
	{
		enabled,
	}
);

const iconSrc = `/${ASSETS_DIR}/img/sports/icons/sport/formula_racing.png`;

const sportContext = computed(() => {
	return new SnowplowSportContext(miniContext.value.sportCategory, miniContext.value.pageType);
});

const pageInfo = computed(() => {
	return overviewData.value?.sportEventBucketsV2?.pageInfo ?? null;
});

const competitions = computed(() => {
	const buckets = overviewData.value?.sportEventBucketsV2?.edges ?? [];

	return (
		buckets
			// HACK: currently getting back a wide date range of events,
			// that need to be additionally filtered on the frontend (until gql is ready)
			.map(bucket => {
				return {
					...bucket,
					node: {
						...bucket.node,
						edges: (bucket.node.edges || [])
							// the whole requesting with PARENT mode is a bit funky.
							// formula 1 parent events have multiple days of duration.
							// that's why we filter the child events out in the frontend until the backend follows suit.
							.filter(({ node }) => {
								type Node = AssertByTypename<typeof node, 'MultiStepSportEvent'>;
								return isSameDate(displayDate.value, new Date((node as Node)?.content.startAt));
							}),
					},
				};
			})
			// filter out empty competitions/buckets that have no events
			.filter(bucket => (bucket.node.edges || []).length > 0)
	);
});

const hasLoadMore = computed(() => {
	return pageInfo.value?.hasNextPage && competitions.value?.length !== 0;
});

onActivated(() => {
	// note: all sport pages are excluded from automatic page tracking
	/**
	 * setTimeout added here to prevent pageView to be triggered before link click track event.
	 * that's because when adding the @click.native to router-link the track event will get triggered
	 * after the navigation which causes the pageView to get triggered first.
	 *
	 */
	setTimeout(() => {
		TrackingHelper.trackPage(route.path, [sportContext.value]);
	});
});

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
	return [
		// e.g. Sport
		{ name: tc('WEBAPP_SPORTS'), routeName: 'app.sports.subdomain.all' },

		// e.g. Motorsport
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_MOTORSPORT'),
			objectType: SportPageType.SportOverview,
			url: getOverviewUrlBySport(miniContext.value.sportCategory, webLocale.value as SportLocale),
		},
	];
});

function handleLoadMore() {
	if (!pageInfo.value?.hasNextPage) return;

	fetchMore({
		variables: {
			after: pageInfo.value?.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;

			const newCompetitions = fetchMoreResult.sportEventBucketsV2.edges ?? [];
			const newPageInfo = fetchMoreResult.sportEventBucketsV2.pageInfo;

			return {
				sportEventBucketsV2: {
					__typename: previousResult.sportEventBucketsV2.__typename,
					edges: [...(previousResult.sportEventBucketsV2.edges ?? []), ...newCompetitions],
					pageInfo: newPageInfo,
				},
			};
		},
	});
}

provide('getEmptyCompetitionHeading', (competition: SportEventBucketsEdgeV2) => {
	return competition.key.competition?.content.name;
});

return { __sfc: true,country, language, localeObject, webLocale, urlMetadata, filters, tc, route, displayDate, filtersSport, motorsportCategory, motorsportTypes, miniContext, queryVariables, enabled, overviewData, isDataLoading, fetchMore, iconSrc, sportContext, pageInfo, competitions, hasLoadMore, breadcrumbs, handleLoadMore, AdUnit, Breadcrumbs, MultiStepEventEncounterList, Header, CompetitionEventsCalendarOld, ProviderStreamsComparator, SportsLayout, Subheading }
}

})